/**
 * DeviceTestMeters
 * Renders a live volume meter under each microphone row, a per-speaker test
 * button + meter under each speaker row, and a live preview under each camera
 * row. One lightweight getUserMedia capture + AudioContext analyser per device
 * (Jitsi-style). Call teardown() before re-mounting or when the sheet closes.
 */
export default class DeviceTestMeters {
    constructor(options = {}) {
        this.barCount = options.barCount || 12;
        this.micStreams = [];
        this.cameraStreams = [];
        this.audioContexts = [];
        // One RAF slot per mic meter keyed by deviceId — never grows unboundedly.
        this._micRafs = {};
        this.testAudio = null;
        this.testSpeakerContext = null;
        this._speakerRaf = null;
        this.activeSpeakerId = null;
        this.testSoundSrc = options.testSoundSrc || '/sounds/test-output.mp3';
        this.playIcon = options.playIcon || '<i class="fas fa-play-circle"></i>';
        this.stopIcon = options.stopIcon || '<i class="fas fa-stop-circle"></i>';
        this._torn = false;
    }

    buildBars(extraClass = '') {
        const wrapper = $('<div/>', { class: `kino-row-meter ${extraClass}` });
        for (let i = 0; i < this.barCount; i++) {
            wrapper.append($('<span/>', { class: 'kino-row-meter-bar' }));
        }
        return wrapper;
    }

    paintBars($wrapper, level) {
        const bars = $wrapper.children('.kino-row-meter-bar');
        const lit = Math.round((level / 100) * bars.length);
        bars.each(function (idx) {
            this.classList.toggle('lit', idx < lit);
        });
    }

    async startMicMeter(deviceId, $barsWrapper) {
        const key = deviceId || '__default__';
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: deviceId ? { deviceId: { exact: deviceId } } : true,
                video: false
            });

            // If teardown() was called while getUserMedia was pending, discard the stream.
            if (this._torn) {
                stream.getTracks().forEach(t => t.stop());
                return;
            }

            this.micStreams.push(stream);

            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            this.audioContexts.push(ctx);
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.smoothingTimeConstant = 0.8;
            analyser.fftSize = 1024;
            source.connect(analyser);

            const data = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                if (this._torn) { return; }
                analyser.getByteFrequencyData(data);
                let sum = 0;
                for (let i = 0; i < data.length; i++) { sum += data[i]; }
                const avg = sum / data.length;
                this.paintBars($barsWrapper, Math.min(100, avg));
                // Overwrite the slot — only one live RAF ID per mic.
                this._micRafs[key] = requestAnimationFrame(tick);
            };
            tick();
        } catch (error) {
            console.error('[DeviceTestMeters] mic meter error', deviceId, error);
            $barsWrapper.addClass('kino-row-meter-unavailable');
        }
    }

    async startCameraPreview(deviceId, videoEl) {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: deviceId ? { deviceId: { exact: deviceId } } : true
            });

            // If teardown() was called while getUserMedia was pending, discard the stream.
            if (this._torn) {
                stream.getTracks().forEach(t => t.stop());
                return;
            }

            this.cameraStreams.push(stream);
            videoEl.srcObject = stream;
            videoEl.muted = true;
            videoEl.playsInline = true;
            try { await videoEl.play(); } catch (e) { /* autoplay guard */ }
        } catch (error) {
            console.error('[DeviceTestMeters] camera preview error', deviceId, error);
            videoEl.classList.add('kino-row-preview-unavailable');
        }
    }

    toggleSpeakerTest(deviceId, $barsWrapper, $iconSpan) {
        if (this.activeSpeakerId === deviceId && this.testAudio && !this.testAudio.paused) {
            this.stopSpeakerTest($iconSpan);
            return;
        }
        this.stopSpeakerTest();

        const audio = new Audio(this.testSoundSrc);
        audio.loop = false;
        this.testAudio = audio;
        this.activeSpeakerId = deviceId;
        this._activeSpeakerIcon = $iconSpan;
        if ($iconSpan) { $iconSpan.html(this.stopIcon); }
        audio.onended = () => this.stopSpeakerTest($iconSpan);

        const CtxClass = window.AudioContext || window.webkitAudioContext;
        // AudioContext.setSinkId routes the whole graph (incl. metering) to the chosen
        // device. Without it, createMediaElementSource() steals the element's output and
        // forces playback to the system default — which is the bug we are fixing.
        const ctxSupportsSink = !!(CtxClass && CtxClass.prototype
            && typeof CtxClass.prototype.setSinkId === 'function');

        // Path A: real metering AND correct output device.
        if (ctxSupportsSink) {
            try {
                const ctx = new CtxClass();
                this.testSpeakerContext = ctx;
                const source = ctx.createMediaElementSource(audio);
                const analyser = ctx.createAnalyser();
                analyser.smoothingTimeConstant = 0.8;
                analyser.fftSize = 1024;
                source.connect(analyser);
                analyser.connect(ctx.destination);
                const data = new Uint8Array(analyser.frequencyBinCount);
                const tick = () => {
                    if (this._torn || !this.testAudio || this.testAudio.paused) { return; }
                    analyser.getByteFrequencyData(data);
                    let sum = 0;
                    for (let i = 0; i < data.length; i++) { sum += data[i]; }
                    this.paintBars($barsWrapper, Math.min(100, sum / data.length));
                    this._speakerRaf = requestAnimationFrame(tick);
                };
                const routeSink = deviceId ? ctx.setSinkId(deviceId).catch(() => {}) : Promise.resolve();
                routeSink
                    .then(() => (ctx.state === 'suspended' ? ctx.resume() : null))
                    .then(() => audio.play())
                    .then(tick)
                    .catch(err => {
                        console.error('[DeviceTestMeters] speaker test play error', err);
                        this.stopSpeakerTest($iconSpan);
                    });
                return;
            } catch (error) {
                console.error('[DeviceTestMeters] speaker test ctx error, falling back', error);
                if (this.testSpeakerContext) {
                    try { this.testSpeakerContext.close(); } catch (e) { /* noop */ }
                    this.testSpeakerContext = null;
                }
            }
        }

        // Path B: fallback — play the element directly on the chosen sink, animate bars.
        const applySink = (deviceId && typeof audio.setSinkId === 'function')
            ? audio.setSinkId(deviceId).catch(() => {})
            : Promise.resolve();
        applySink
            .then(() => audio.play())
            .then(() => this._animateFallbackBars($barsWrapper))
            .catch(err => {
                console.error('[DeviceTestMeters] speaker test play error', err);
                this.stopSpeakerTest($iconSpan);
            });
    }

    // Decorative bar pulse for browsers where we can't meter the output graph.
    _animateFallbackBars($barsWrapper) {
        let last = 0;
        const animate = (ts) => {
            if (this._torn || !this.testAudio || this.testAudio.paused) { return; }
            if (!last || ts - last > 90) {
                last = ts;
                this.paintBars($barsWrapper, 30 + Math.random() * 60);
            }
            this._speakerRaf = requestAnimationFrame(animate);
        };
        this._speakerRaf = requestAnimationFrame(animate);
    }

    stopSpeakerTest($iconSpan) {
        const icon = $iconSpan || this._activeSpeakerIcon;
        try {
            if (this.testAudio) {
                this.testAudio.pause();
                this.testAudio.currentTime = 0;
            }
        } catch (e) { /* noop */ }
        if (this.testSpeakerContext) {
            try { this.testSpeakerContext.close(); } catch (e) { /* noop */ }
            this.testSpeakerContext = null;
        }
        if (icon) { icon.html(this.playIcon); }
        this.testAudio = null;
        this.activeSpeakerId = null;
        this._activeSpeakerIcon = null;
    }

    /**
     * containerSelector: jQuery container holding the rendered audio sheet.
     * Mic rows: a.audio-input-device / button.audio-input-device with [device-id].
     * Speaker rows: .audio-output-device with [device-id].
     */
    mountAudioSheet($container) {
        const self = this;
        $container.find('.audio-input-device[device-id]').each(function () {
            const deviceId = $(this).attr('device-id');
            if (!deviceId) { return; }
            const bars = self.buildBars('kino-row-meter-mic');
            $(this).after(bars);
            self.startMicMeter(deviceId, bars);
        });
        $container.find('.audio-output-device[device-id]').each(function () {
            const deviceId = $(this).attr('device-id');
            if (!deviceId) { return; }
            const iconSpan = $('<span/>', { class: 'kino-row-test-icon', html: self.playIcon });
            const testBtn = $('<button/>', {
                type: 'button',
                class: 'kino-row-test-btn',
                html: [iconSpan, $('<span/>', { html: 'Test' })]
            });
            const bars = self.buildBars('kino-row-meter-speaker');
            const row = $('<div/>', { class: 'kino-row-speaker-test', html: [testBtn, bars] });
            $(this).after(row);
            testBtn.on('click', e => {
                e.preventDefault();
                e.stopPropagation();
                self.toggleSpeakerTest(deviceId, bars, iconSpan);
            });
        });
    }

    /**
     * Camera rows: a.video-input-device / button.video-input-device with [device-id].
     * To save vertical space, the camera name is overlaid on the preview tile and
     * the original device row is hidden (the selected state is mirrored onto the tile).
     */
    mountVideoSheet($container) {
        const self = this;
        $container.find('.video-input-device[device-id]').each(function () {
            const $row = $(this);
            const deviceId = $row.attr('device-id');
            if (!deviceId) { return; }
            const label = ($row.find('.ellipsis').text() || $row.text() || '').trim();
            const isSelected = $row.hasClass('selected');

            const video = document.createElement('video');
            video.autoplay = true;
            video.muted = true;
            video.playsInline = true;
            video.classList.add('flipVideoX', 'kino-row-preview');

            const nameOverlay = $('<div/>', {
                class: 'kino-row-preview-name',
                html: `<i class="fas fa-video mr-1"></i><span class="ellipsis">${label}</span>`
            });

            const wrapper = $('<div/>', {
                class: `kino-row-preview-wrapper ${isSelected ? 'selected' : ''}`,
                'device-id': deviceId
            });
            wrapper.append(video, nameOverlay);

            // Selecting a camera: clicking the tile triggers the original row's handler.
            wrapper.on('click', e => {
                e.preventDefault();
                e.stopPropagation();
                $row.trigger('click');
            });

            $row.after(wrapper);
            $row.addClass('d-none');
            self.startCameraPreview(deviceId, video);
        });
    }

    teardown() {
        // Set torn first so any pending async startMicMeter resolves bail out immediately.
        this._torn = true;

        // Cancel all per-mic RAF loops.
        Object.values(this._micRafs).forEach(id => cancelAnimationFrame(id));
        this._micRafs = {};

        // Cancel speaker test RAF.
        if (this._speakerRaf) { cancelAnimationFrame(this._speakerRaf); this._speakerRaf = null; }

        this.micStreams.forEach(s => s.getTracks().forEach(t => t.stop()));
        this.micStreams = [];
        this.cameraStreams.forEach(s => s.getTracks().forEach(t => t.stop()));
        this.cameraStreams = [];
        this.audioContexts.forEach(c => { try { c.close(); } catch (e) { /* noop */ } });
        this.audioContexts = [];
        this.stopSpeakerTest();
    }
}
